import { CaseDocumentType } from 'enums/case-document-types';
import queryString from 'query-string';
import apiService from '../apiService';

const baseUrl = `${window._env_.PLATFORM_API_DOMAIN}`;
const baseUrlLegacy = `${window._env_.API_DOMAIN}`;
const marketplaceUrl = `${window._env_.MARKETPLACE_API_DOMAIN}`;
const businessUrl = `${window._env_.BUSINESS_API_DOMAIN}`;

const getFundingSearchById = (id, extraParams = { includeMatches: true, includeRules: true }) => {
  const url = `${baseUrl}/v1/funding/searches/${id}`;
  return apiService.get(queryString.stringifyUrl({ url, query: extraParams }));
};

const getFundingSearchProfileById = (id, extraParams = {}) => {
  const url = `${baseUrlLegacy}/v1/funding/search/${id}/profile`;
  return apiService.get(queryString.stringifyUrl({ url, query: extraParams }));
};

const getProfileByEntityId = (id) => {
  const url = `${baseUrl}/v1/profiles/entity/${id}`;
  return apiService.get(url);
};

const postAffordabilityProfileByEntityId = (id, payload) => {
  const url = `${baseUrl}/v1/profiles/entity/${id}/funding-affordability`;
  return apiService.post(url, payload);
};

const patchProfileData = (id, payload) => {
  const url = `${baseUrl}/v1/profiles/${id}`;
  return apiService.patch(url, payload);
};

const patchAffordabilityProfileData = (id, payload) => {
  const url = `${baseUrl}/v1/profiles/${id}/funding-affordability`;
  return apiService.patch(url, payload);
};

const postMaxBorrowAmount = (payload) => {
  const url = `${baseUrl}/v1/funding/calculator/max-borrow-amount`;
  return apiService.post(url, payload);
};

const postRepayments = (payload) => {
  const url = `${baseUrl}/v1/funding/calculator/repayments`;
  return apiService.post(url, payload);
};

const getProfileSectionData = (id, section) => {
  const url = `${baseUrl}/v1/profiles/${id}/${section}`;
  return apiService.get(url);
};
const getProfileDocumentsRules = (id, type = CaseDocumentType.forApplication) => {
  const url = `${baseUrl}/v1/profiles/${id}/documents/types/${type}/to-upload`;
  return apiService.get(url);
};
const getProfileDocumentsUploaded = (id, type = CaseDocumentType.forApplication) => {
  const url = `${baseUrl}/v1/profiles/${id}/documents/types/${type}`;
  return apiService.get(url);
};
const getProfileDownloadAllDocuments = (id, type = CaseDocumentType.forApplication) => {
  const url = `${baseUrl}/v1/profiles/${id}/documents/types/${type}/download`;
  return apiService.get(url);
};

const getProfileDownloadCsv = (id) => {
  const url = `${baseUrl}/v1/profiles/${id}/export`;
  return apiService.get(url);
};

const postProfileDocumentsUploaded = ({
  profileId,
  type = CaseDocumentType.forApplication,
  group,
  file,
  progressCallback,
}) => {
  const url = `${baseUrl}/v1/profiles/${profileId}/documents/types/${type}/${group}`;

  const formData = new FormData();
  formData.append('file', file);

  return apiService.post(url, formData, {
    responseType: 'text',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      progressCallback?.(progressEvent);
    },
  });
};
const deleteProfileDocumentsUploaded = (id, documentId) => {
  const url = `${baseUrl}/v1/profiles/${id}/documents/${documentId}`;
  return apiService.delete(url);
};
const getProfileSectionJsonSchema = (section) => {
  const url = `${baseUrl}/v1/profiles/${section}/json-schema`;
  return apiService.get(url);
};
const putProfileSectionData = (id, section, payload) => {
  const url = `${baseUrl}/v1/profiles/${id}/${section}`;
  return apiService.put(url, payload);
};
const postDirectorSection = (id, payload) => {
  const url = `${baseUrl}/v1/profiles/${id}/director-details`;
  return apiService.post(url, payload);
};
const putDirectorSection = (id, directorId, payload) => {
  const url = `${baseUrl}/v1/profiles/${id}/director-details/${directorId}`;
  return apiService.put(url, payload);
};
const deleteDirectorSection = (id, directorId) => {
  const url = `${baseUrl}/v1/profiles/${id}/director-details/${directorId}`;
  return apiService.delete(url);
};
const patchProfileDocumentsSetComplete = (id, type = CaseDocumentType.forApplication) => {
  const url = `${baseUrl}/v1/profiles/${id}/documents/types/${type}/set-complete`;
  return apiService.patch(url);
};

const getProfileByFundingSearchId = (fundingSearchId) => {
  const url = `${baseUrl}/v1/profiles/funding-search/${fundingSearchId}`;
  return apiService.get(url);
};

const getProfileLenderSelection = (id) => {
  const url = `${baseUrl}/v1/profiles/${id}/applications`;
  return apiService.get(url);
};

const getProfileByApplicationId = (id) => {
  const url = `${businessUrl}/v1/applications/${id}/sign-up-details`;
  return apiService.get(url);
};

const postProfileLenderSelection = (id, payload = []) => {
  const url = `${baseUrl}/v1/profiles/${id}/applications`;
  return apiService.post(url, payload);
};

const cancelCaseById = (id, isFundingSearch = true) => {
  const url = `${baseUrl}/v1/${isFundingSearch ? 'funding/searches' : 'cases'}/${id}/cancel`;
  return apiService.post(url);
};

const applyCaseById = (id) => {
  const url = `${marketplaceUrl}/v1/profiles/${id}/cases/apply`;
  return apiService.post(url, {}, { marketplace: true });
};

const deleteCaseById = (id) => {
  const url = `${baseUrl}/v1/entities/${id}/all`;
  return apiService.delete(url);
};

const setCasePrivacy = (id, payload) => {
  const url = `${baseUrl}/v1/funding/searches/${id}/privacy`;
  return apiService.patch(url, payload);
};

const getFundingAffordabilityProfileByEntityId = (id) => {
  const url = `${baseUrl}/v1/profiles/entity/${id}/funding-affordability`;
  return apiService.get(url);
};

const getMatchResultsAverageProfileId = (id) => {
  const url = `${baseUrl}/v1/profiles/${id}/funding/results/average`;
  return apiService.get(url);
};

const getQuestionnaireEligibility = (questionnaireType, entityId) => {
  const url = `${baseUrl}/v1/questionnaires/${questionnaireType}/eligibility/${entityId}`;
  return apiService.get(url);
};

export {
  deleteCaseById,
  getFundingSearchById,
  getFundingSearchProfileById,
  getProfileByFundingSearchId,
  cancelCaseById,
  patchProfileData,
  setCasePrivacy,
  getProfileSectionData,
  getProfileDocumentsRules,
  getProfileDownloadAllDocuments,
  getProfileDocumentsUploaded,
  postProfileDocumentsUploaded,
  patchProfileDocumentsSetComplete,
  deleteProfileDocumentsUploaded,
  getProfileSectionJsonSchema,
  putProfileSectionData,
  postDirectorSection,
  putDirectorSection,
  deleteDirectorSection,
  getProfileLenderSelection,
  postProfileLenderSelection,
  applyCaseById,
  getProfileByApplicationId,
  getProfileDownloadCsv,
  getProfileByEntityId,
  getFundingAffordabilityProfileByEntityId,
  postAffordabilityProfileByEntityId,
  patchAffordabilityProfileData,
  getMatchResultsAverageProfileId,
  postMaxBorrowAmount,
  postRepayments,
  getQuestionnaireEligibility,
};
